import { Injectable } from '@angular/core';
import type { Observable } from 'rxjs';
import { fromEvent } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, startWith } from 'rxjs/operators';
import { BootstrapBreakPointsService } from './bootstrap-breakpoints.service';

export enum BreakPoint {
  EXTRA_SMALL = 'EXTRA_SMALL',
  COMPACT = 'COMPACT',
  MEDIUM = 'MEDIUM',
  LARGE = 'LARGE',
  EXTRA_LARGE = 'EXTRA_LARGE'
}

@Injectable({ providedIn: 'root' })
export class ResponsiveLayoutService {
  private readonly resizeEvent$: Observable<number> = fromEvent(window, 'resize', { passive: true }).pipe(
    startWith(new Event('resize')),
    map(event => (event?.target as Window)?.innerWidth ?? window?.innerWidth),
    shareReplay({ bufferSize: 1, refCount: false })
  );
  private readonly layouts: Partial<Record<BreakPoint, Observable<boolean>>> = {};

  isBreakPoint$(breakpoint: BreakPoint): Observable<boolean> {
    if (!this.layouts[breakpoint]) {
      this.layouts[breakpoint] = this.resizeEvent$.pipe(
        map(width => this.isWidthWithinBreakpoint(breakpoint, width)),
        distinctUntilChanged(),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }

    return this.layouts[breakpoint];
  }

  private isWidthWithinBreakpoint(breakpoint: BreakPoint, width?: number): boolean {
    if (width === undefined || width === null) {
      return false;
    }

    if (breakpoint === BreakPoint.EXTRA_SMALL) {
      return BootstrapBreakPointsService.isXS(width);
    } else if (breakpoint === BreakPoint.COMPACT) {
      return BootstrapBreakPointsService.isXS(width) || BootstrapBreakPointsService.isSM(width);
    } else if (breakpoint === BreakPoint.MEDIUM) {
      return BootstrapBreakPointsService.isMD(width);
    } else if (breakpoint === BreakPoint.LARGE) {
      return BootstrapBreakPointsService.isLG(width);
    } else if (breakpoint === BreakPoint.EXTRA_LARGE) {
      return BootstrapBreakPointsService.isStrictXL(width);
    }

    return false;
  }
}
